import { useCallback } from 'react';
import { toast } from 'sonner';
import { playCheckSound } from './sound';

/**
 * Hook helper: optimistic toggle di un campo booleano in una lista locale.
 *
 * Uso:
 *   const [items, setItems] = useState([...]);
 *   const toggle = useOptimisticToggle(setItems, persistFn, 'fatto');
 *   <Checkbox checked={item.fatto} onToggle={(next) => toggle(item.id, next)} />
 *
 * Comportamento:
 *  - aggiorna la lista locale immediatamente (setItems((prev) => prev.map ...))
 *  - chiama persistFn(id, next) in background
 *  - se persistFn fallisce → rollback + toast d'errore
 *  - se la transizione è false → true riproduce il suono (rispetta mute)
 *  - mostra toast "Fatto · Annulla" con countdown 5s che ripristina lo stato precedente
 *    sia in locale sia su backend (riusa persistFn con il valore originale)
 *  - nessun re-fetch globale
 */
export function useOptimisticToggle(setItems, persistFn, field = 'fatto', label = 'Aggiornato') {
  return useCallback(async (id, next) => {
    const previous = {};
    setItems((prev) => prev.map((it) => {
      if (it.id === id) {
        previous[id] = it[field];
        return { ...it, [field]: next };
      }
      return it;
    }));

    if (next && previous[id] !== true) {
      playCheckSound();
    }

    try {
      await persistFn(id, next);
    } catch (e) {
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, [field]: previous[id] } : it)));
      const msg = e?.response?.data?.detail || e?.message || 'errore';
      toast.error(`Salvataggio fallito · ${msg}`);
      return;
    }

    // Successo → toast con "Annulla"
    const prevValue = previous[id];
    toast(`${label} · ${next ? 'fatto' : 'riaperto'}`, {
      duration: 5000,
      action: {
        label: 'Annulla',
        onClick: async () => {
          // Optimistic revert locale
          setItems((prev) => prev.map((it) => (it.id === id ? { ...it, [field]: prevValue } : it)));
          try {
            await persistFn(id, prevValue);
            toast.success('Ripristinato', { duration: 2000 });
          } catch (err) {
            // Re-apply lo stato che era andato a buon fine
            setItems((prev) => prev.map((it) => (it.id === id ? { ...it, [field]: next } : it)));
            toast.error('Undo fallito');
          }
        },
      },
    });
  }, [setItems, persistFn, field, label]);
}
